<script setup>
import { ref, computed, inject, onMounted, watch } from 'vue';
import * as navConfig from '@/navConfig';
import { router } from '@inertiajs/vue3';
import { useDisplay } from 'vuetify';
import { useUserStore } from '@/stores/user';
import { useOptionsStore } from '@/stores/options';
import { useLabStore } from '@/stores/lab';
import { usePharmacyStore } from '@/stores/pharmacy';
import { storeToRefs } from 'pinia';
import { isRole } from '@/utils/roleHelper';
import { loadLanguageAsync } from 'laravel-vue-i18n';

defineOptions({
    inheritAttrs: false
});

const drawer = ref(true);
const rail = ref(false);
const { thresholds } = useDisplay();

const { profile, lastLogin, locale } = storeToRefs(useUserStore());
const { securityCheckpoint } = storeToRefs(useOptionsStore());
const roles = computed(() => isRole(profile.value));
const { stopAccessLab } = useLabStore();
const { stopAccessPharmacy } = usePharmacyStore();

const isLoading = inject('isLoading');

const routes = computed(() => {
    let allRoutes = [];
    
    if (roles.value.superAdmin()) {
        if (profile.value.is_accessing_lab && !profile.value.is_accessing_pharmacy) {
            allRoutes.push(...navConfig.accessingLab());
        } else if (profile.value.is_accessing_pharmacy) {
            allRoutes.push(...navConfig.accessingPharmacy());
        } else {
            allRoutes.push(...navConfig.superAdmin());
        }
    }

    if (roles.value.systemAdmin()) {
        if (profile.value.is_accessing_lab && !profile.value.is_accessing_pharmacy) {
            allRoutes.push(...navConfig.accessingLab());
        } else if (profile.value.is_accessing_pharmacy) {
            allRoutes.push(...navConfig.accessingPharmacy());
        } else {
            allRoutes.push(...navConfig.systemAdmin());
        }
    }

    if (roles.value.labAdmin()) {
        if (profile.value.is_accessing_pharmacy) {
            allRoutes.push(...navConfig.accessingPharmacy());
        } else {
            allRoutes.push(...navConfig.labAdmin());
        }
    }

    if (roles.value.labStaff()) {
        if (profile.value.is_accessing_pharmacy) {
            allRoutes.push(...navConfig.accessingPharmacy());
        } else {
            allRoutes.push(...navConfig.labStaff());
        }
    }

    if (roles.value.pharmacyStaff()) {
        allRoutes.push(...navConfig.pharmacyStaff());
    }

    if (roles.value.pharmacyAdmin()) {
        allRoutes.push(...navConfig.pharmacyAdmin());
    }

    const uniqueRoutes = allRoutes.reduce((acc, current) => {
        const x = acc.find(item => item.name === current.name);
        return x ? acc : [...acc, current];
    }, []);

    return uniqueRoutes;
});

const bottomNavRoutes = computed(() => {
    if (roles.value.superAdmin()) {
        return navConfig.bottomNavRoutes();
    } else {
        return navConfig.bottomNavRoutes().filter(x => !x.private);
    }
});

const isLocal = computed(() => {
    return import.meta.env.VITE_ENV === 'local';
});

const isAccessingLab = computed(() => {
    if (profile.value) {
        return profile.value.is_accessing_lab;
    }
    return false;
});

const isAccessingPharmacy = computed(() => {
    if (profile.value) {
        return profile.value.is_accessing_pharmacy;
    }
    return false;
});

onMounted(() => {
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
});

const logout = async () => {
    await router.post('/logout');
};

const changeBar = () => {
    if (window.innerWidth <= thresholds.value.md) {
        rail.value = false;
    }
    drawer.value = !drawer.value;
};

const stopAccessing = async () => {
    if (isAccessingPharmacy.value) {
        await stopAccessPharmacy();
        if (roles.value.superAdmin() || roles.value.systemAdmin()) {
            router.visit('/orders');
        } else if (roles.value.labAdmin() || roles.value.labStaff()) {
            router.visit('/orders');
        }
    } else if (isAccessingLab.value) {
        await stopAccessLab();
        router.visit('/labs');
    }
};

const activeRoute = ref(null);

const visitPath = (route) => {
    activeRoute.value = route.path;
    if (route.private) {
        window.open(route.path, '_blank');
    } else {
        router.visit(route.path);
    }
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
};

const { setLocale } = useUserStore();
watch(locale, (lang) => {
    loadLanguageAsync(lang);
});

// Get lab logo
const logo = computed(() => {
    const defaultLogo = '/images/logo.png';
    const activeLab = profile.value?.active_lab;

    return activeLab?.logo_path ? `/storage/${activeLab.logo_path}` : defaultLogo;
});
</script>

<template>
<div>
    <v-app>
        <v-navigation-drawer
            v-if="!securityCheckpoint"
            v-model="drawer"
            :rail="rail"
            mobile-breakpoint="md"
            app>
            <div v-if="isAccessingLab || isAccessingPharmacy" class="border rounded-lg text-center m-4 p-2 cursor-pointer shadow-lg bg-primary hover:bg-primary-light transition-colors text-white" @click="stopAccessing">
                <div v-if="isAccessingLab && !isAccessingPharmacy" class="text-base">{{ profile.active_lab.name }}</div>
                <div v-if="isAccessingPharmacy" class="truncate">{{ profile.active_pharmacy.name }}</div>
                <div class="text-xs italic">{{ $t('label.click-here-to-stop-accessing') }}</div>
            </div>
            <v-list>
                <img
                    :src="rail ? '/images/icon-logo.png' : logo"
                    :class="rail ? 'mx-auto px-2 pt-1.5' : 'w-full px-4 object-contain h-14'"
                    alt="Logo"
                    class="cursor-pointer"
                    @click="$inertia.visit('/')">
            </v-list>

            <v-list class="nav">
                <div class="hidden md:flex" :class="!rail ? 'justify-end' : 'justify-center'">
                    <v-app-bar-nav-icon
                        v-tooltip.right="rail ? 'Open' : 'Minimize'"
                        color="accent"
                        :icon="!rail ? 'mdi-arrow-collapse-left' : 'mdi-arrow-collapse-right'"
                        @click="rail = !rail" />
                </div>
                <v-list-item
                    v-for="route in routes"
                    :key="route.name"
                    v-tooltip.right="!drawer ? '' : route.label"
                    :class="{'v-list-item--active': route.name === $page.component}"
                    :variant="route.name === $page.component ? 'tonal' : 'text'"
                    :title="route.label"
                    :value="route.name"
                    @click="visitPath(route)">
                    <template #prepend>
                        <v-progress-circular
                            v-if="isLoading && route.path === activeRoute"
                            :width="3"
                            :size="24"
                            class="mr-8"
                            color="secondary"
                            indeterminate />
                        <v-icon v-else-if="activeRoute === route.path" :icon="route.icon" color="secondary" />
                        <v-icon v-else :icon="route.icon" />
                    </template>
                </v-list-item>
            </v-list>

            <template v-if="!securityCheckpoint" #append>
                <div class="border-t border-grey-500">
                    <v-list>
                        <v-list-item
                            v-for="route in bottomNavRoutes"
                            :key="route.name"
                            v-tooltip.right="!drawer ? '' : route.label"
                            :class="{'v-list-item--active': route.name === $page.component}"
                            :variant="route.name === $page.component ? 'tonal' : 'text'"
                            :title="route.label"
                            :value="route.name"
                            @click="visitPath(route)">
                            <template #prepend>
                                <v-progress-circular
                                    v-if="isLoading && route.path === activeRoute"
                                    :width="3"
                                    :size="24"
                                    class="mr-8"
                                    color="secondary"
                                    indeterminate />
                                <v-icon v-else-if="activeRoute === route.path" :icon="route.icon" color="secondary" />
                                <v-icon v-else :icon="route.icon" />
                            </template>
                        </v-list-item>

                        <v-list-item
                            v-if="isLocal"
                            v-tooltip.right="!drawer ? '' : 'Project Setup'"
                            prepend-icon="mdi-magnify"
                            title="Project Setup"
                            @click.prevent="$inertia.visit('/local/project')">
                            <template #prepend>
                                <v-progress-circular
                                    v-if="isLoading && activeRoute === '/local/project'"
                                    :width="3"
                                    :size="24"
                                    class="mr-8"
                                    color="secondary"
                                    indeterminate />
                                <v-icon v-else-if="$page.component === 'Local/ProjectSetup'" icon="mdi-magnify" color="secondary" />
                                <v-icon v-else icon="mdi-magnify" />
                            </template>
                        </v-list-item>
                        <v-list-item
                            v-tooltip.right="!drawer ? '' : 'Sign Out'"
                            prepend-icon="mdi-power-standby"
                            title="Sign Out"
                            value="logout"
                            @click.prevent="logout">
                            <template #prepend>
                                <v-icon icon="mdi-power-standby" />
                            </template>
                        </v-list-item>
                    </v-list>
                </div>
            </template>
        </v-navigation-drawer>

        <v-app-bar v-if="!securityCheckpoint" app>
            <div class="block md:hidden ml-4">
                <v-app-bar-nav-icon @click="changeBar" />
            </div>
            <div class="flex justify-items-end justify-end w-full mr-6">
                <img
                    class="md:hidden block h-12 object-scale-down"
                    src="/images/logo.png"
                    alt="Logo">
            </div>
        </v-app-bar>

        <v-main app>
            <v-container fluid>
                <slot />
            </v-container>
        </v-main>

        <div class="z-[1008]">
            <modals-container />
        </div>

        <v-footer app>
            <div class="mb-0 flex justify-between w-full items-center mx-4 text-sm">
                <div class="italic">{{ $t('auth.last-login', { 'lastLogin': lastLogin }) }}</div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div>Copyright &copy; Fabled Solutions {{ new Date().getFullYear() }}</div>
                <v-btn-toggle
                    :model-value="locale"
                    class="!h-6 min-w-20 ml-2"
                    variant="outlined"
                    color="primary"
                    mandatory
                    group
                    @update:model-value="setLocale">
                    <v-btn value="en">{{ $t('common.en') }}</v-btn>
                    <v-btn value="fr">{{ $t('common.fr') }}</v-btn>
                </v-btn-toggle>
            </div>
        </v-footer>
    </v-app>
</div>
</template>
